import * as React from 'react';
import { useMemo, useSyncExternalStore } from 'react';
import Icon from '@cx-web/components/primitives/icon';
import { type BetslipProviderData, useBetslipContext } from '@cx-web/feature-betslip/context';
import {
  type BetslipStoreState,
  getActiveBetslipStore,
  getBetslipSnapshot,
  subscribeToActiveBetslipStore,
} from '@cx-web/feature-betslip/store';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS, trackingActions } from '@cx-web/feature-tracking';
import classNames from 'classnames';

import { buildBetSlipViewEvent } from '../../context/track-betslip-view';
import styles from '../style/mini-betslip.module.css';

// The slice of betslip data MiniBetslip actually consumes. Both the context-backed (withContext)
// and store-backed (withStore) HOCs satisfy this; the full BetslipProviderData returned by
// useBetslipContext() is assignable to it.
type MiniBetslipContext = Pick<
  BetslipProviderData,
  'added' | 'betslip' | 'count' | 'open' | 'stake' | 'toggleAdded' | 'toggleBetslip' | 'trackBetSlip'
>;

interface WithContext {
  betslipContext: MiniBetslipContext;
}

// Presentation variant, named by what it renders rather than by any one consumer's flag state:
//  - 'pill' (default): the icon + "BETSLIP" text on a dark pill. Used by Product Plus (which owns
//    this look permanently and declares it explicitly) and by the UK header while the restyle is OFF.
//  - 'icon': the icon-only restyle (OCGC-3133), part of the UK header restyle (NGF-6475). atlas /
//    atlas-astro map their RENDER_UK_HEADER_STYLE flag to this variant.
// Deliberately not a boolean: when the UK header restyle fully lands and the pre-restyle look is
// deprecated *for the UK header*, the 'pill' variant stays a first-class option for its other
// consumers — dropping it would be a visible, type-checked change, not a silent default flip.
export type MiniBetslipVariant = 'icon' | 'pill';

interface IMiniBetslipProps extends WithContext {
  betslipTitle: string;
  className?: string;
  variant?: MiniBetslipVariant;
}

interface IMiniBetslipState {
  canRenderBetCount: boolean;
}

// Header toggle that opens/closes the betslip drawer. Exposes aria-expanded
// for state and uses `betslipTitle` as its accessible name. Full ARIA
// navigation contract for tests/AT lives at the top of ../betslip.tsx.
class MiniBetslip extends React.Component<IMiniBetslipProps, IMiniBetslipState> {
  constructor(props: IMiniBetslipProps) {
    super(props);

    this.state = {
      canRenderBetCount: false,
    };
  }

  public componentDidMount(): void {
    this.setState({ canRenderBetCount: true });
  }

  public shouldComponentUpdate(nextProps: IMiniBetslipProps, nextState: IMiniBetslipState) {
    const { canRenderBetCount } = this.state;
    const { canRenderBetCount: nextCanRenderBetCount } = nextState;
    const { added, betslip, count, open } = this.props.betslipContext;
    const { added: nextAdded, betslip: nextBetslip, count: nextCount, open: nextOpen } = nextProps.betslipContext;

    return (
      nextCanRenderBetCount !== canRenderBetCount ||
      nextBetslip !== betslip ||
      nextCount !== count ||
      nextAdded !== added ||
      nextOpen !== open
    );
  }

  private handleBetslipToggle = () => {
    const { betslipContext } = this.props;
    const { toggleBetslip } = betslipContext;

    trackingActions.eventTracking({
      event: TRACKING_EVENTS.BETSLIP_ICON_CLICKED,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BETSLIP_ICON_CLICKED],
    });

    const { betslip, stake } = betslipContext;
    toggleBetslip();
    betslipContext.trackBetSlip(betslip, stake);
  };

  public componentDidUpdate(prevProps: IMiniBetslipProps) {
    const { added: betAdded, count, toggleAdded } = this.props.betslipContext;
    const { count: prevCount } = prevProps.betslipContext;

    if (count > prevCount) {
      toggleAdded(true);
    }

    if (betAdded) {
      setTimeout(() => {
        toggleAdded(false);
      }, 400);
    }
  }

  private renderBetCount(): React.JSX.Element | null {
    const { canRenderBetCount } = this.state;
    const { added: betAdded, count: betCount } = this.props.betslipContext;
    if (!canRenderBetCount || !betCount) {
      return null;
    }

    return <span className={[styles.betCount, betAdded ? styles.betAdded : ''].join(' ')}>{betCount}</span>;
  }

  public render(): React.JSX.Element {
    const { betslipTitle, className, variant = 'pill' } = this.props;
    const { count, open } = this.props.betslipContext;
    const accessibleName = count > 0 ? `${betslipTitle}, ${count} ${count === 1 ? 'bet' : 'bets'}` : betslipTitle;

    // 'pill' (default) renders the icon + "BETSLIP" text on a pill; 'icon' is the UK restyle icon-only
    // trigger (NGF-6475). Both wrapper looks are explicit modifiers — neither is the unnamed default —
    // so removing one never silently restyles the other.
    const isPill = variant === 'pill';

    return (
      <div
        aria-expanded={open}
        aria-label={accessibleName}
        className={classNames(styles.wrapper, isPill ? styles.wrapperPill : styles.wrapperIcon)}
        data-testid="betslip-button"
        onClick={this.handleBetslipToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleBetslipToggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={classNames(styles.miniBetslip, className)}>
          <Icon height={20} icon="Betslip" width={20} />
          {this.renderBetCount()}
        </div>
        {isPill && <span className={styles.betslipText}>{betslipTitle}</span>}
      </div>
    );
  }
}

export function withContext<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    const betslipContext = useBetslipContext();

    return <Component {...(props as T)} betslipContext={betslipContext} />;
  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withContext(${displayName})`;

  return WrappedComponent;
}

const MiniBetslipWithContextAndStoreSlices = withContext(MiniBetslip);

export default MiniBetslipWithContextAndStoreSlices;

// Zero-state used until a store is published — mirrors createBetslipStore's defaults (stake: 10).
// Frozen + module-scoped so getEmptyState returns a stable reference, satisfying
// useSyncExternalStore's snapshot contract when no store is active.
const EMPTY_STATE: BetslipStoreState = Object.freeze({
  added: false,
  betslip: [],
  bookieOdds: [],
  clickoutErrorCode: null,
  loading: false,
  open: false,
  selectedBookie: null,
  stake: 10,
});
const getEmptyState = (): BetslipStoreState => EMPTY_STATE;
const noopSubscribe = () => () => {};

/**
 * Store-backed counterpart to withContext: reads the published global betslip store via
 * useSyncExternalStore instead of requiring a BetslipProvider ancestor. Lets the header badge render
 * in a React root separate from the one that owns BetslipProvider (e.g. Astro's CxHeaderWrapper /
 * BetslipShell split), or as a documented escape hatch in RRv7.
 *
 * Renders a zero-state badge until a store is published, then reflects it reactively. Writes
 * (open/added) go straight to the shared store, so the sibling root's provider sees them.
 */
export function withStore<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    // Layer 1 — subscribe to the active-store *pointer*. This re-renders us when BetslipProvider
    // publishes (or clears) its store, including the case where it mounts *after* we already rendered
    // (the header island hydrating before BetslipShell in Astro). getActiveBetslipStore is the stable
    // snapshot — it returns the same reference until setActiveBetslipStore swaps it; null on the server.
    const store = useSyncExternalStore(subscribeToActiveBetslipStore, getActiveBetslipStore, getActiveBetslipStore);

    // Layer 2 — subscribe to state *within* the active store. When the pointer changes (layer 1),
    // this re-subscribes to the new store's subscribe/getState. Pass the raw, reference-stable getState
    // (not a freshly-derived snapshot) and derive via useMemo — the same pattern BetslipProvider uses
    // to avoid an infinite render loop from a new object on every getSnapshot call.
    const state = useSyncExternalStore(
      store?.subscribe ?? noopSubscribe,
      store?.getState ?? getEmptyState,
      store?.getState ?? getEmptyState,
    );
    const snapshot = useMemo(() => getBetslipSnapshot(state), [state]);

    // Memoise so an unrelated parent re-render doesn't churn a fresh context object (and fresh action
    // closures) every pass — it only changes when the derived snapshot does. The actions re-fetch the
    // live store via getActiveBetslipStore() at call time rather than closing over the render-time `store`,
    // so they always target the currently-published store even if the pointer swapped since this memo ran
    // (which also keeps memoising on `[snapshot]` correct).
    const betslipContext = useMemo<MiniBetslipContext>(
      () => ({
        ...snapshot,
        toggleAdded: (val?: boolean) => {
          const s = getActiveBetslipStore();
          s?.setState({ added: val ?? !s.getState().added });
        },
        toggleBetslip: (val?: boolean) => {
          const s = getActiveBetslipStore();
          s?.setState({ open: val ?? !s.getState().open });
        },
        // Emit the same BET_SLIP_VIEW event the context path fires, via the shared payload builder so the
        // two header variants stay byte-identical. The store carries the betslip + stake this needs, so
        // no provider/BetslipUtils dependency is required. See NGF-6313.
        trackBetSlip: (betSlip, stake) => trackingActions.eventTracking(buildBetSlipViewEvent(betSlip, stake)),
      }),
      [snapshot],
    );

    return <Component {...(props as T)} betslipContext={betslipContext} />;
  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withStore(${displayName})`;

  return WrappedComponent;
}

export const MiniBetslipFromStore = withStore(MiniBetslip);
